/**
 * Vesper useCleo Hook (Shared)
 *
 * Uses crypto.randomUUID() which is available in both web and React Native.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { sendArkMessage, selectExternalVenue, skipExternalSearch, selectVenueFromSuggestions, rejectVenueSuggestions as rejectVenuesApi, abandonSession as abandonSessionApi, abandonActiveSession as abandonActiveSessionApi, completeSession as completeSessionApi, resumeSession as resumeSessionApi } from '../services/cleo.service';
import { createEventPrompt } from '../services/event_prompt.service';
import { getApiBase } from '../constants/api';
import { getStorage, resolveStorageValue } from '../storage/storage';
import type {
  ArkChatMessage, ArkChatResponse, EventPlanPayload,
  ExternalSearchResult, YelpVenue, TicketmasterEvent,
  PlaceSuggestion,
} from '../types/ark.types';
import type { ActivityType, EventPromptWithCandidates } from '../types/event_prompt.types';

const VALID_ACTIVITY_TYPES: string[] = [
  'pitch_practice', 'investor_coffee', 'peer_mastermind', 'demo_review',
  'working_session', 'hiring_chat', 'topic_salon',
  'dinner', 'coffee', 'other',
];

/** IANA timezone for the current device (e.g., "America/New_York").
 *  Used to anchor Cleo's timeStartISO output so "Friday at 5 PM" is stored
 *  as the user's local 5 PM rather than 5 PM UTC. */
function resolveTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function useCleo(resetKey?: string) {
  const [messages, setMessages] = useState<ArkChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestedAction, setSuggestedAction] = useState<ArkChatResponse['suggestedAction']>(null);
  const [planningTurn, setPlanningTurn] = useState<number | null>(null);
  const [maxPlanningTurns, setMaxPlanningTurns] = useState<number | null>(null);

  const [eventPlan, setEventPlan] = useState<EventPlanPayload | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const [externalResults, setExternalResults] = useState<ExternalSearchResult | null>(null);
  const [planningSessionId, setPlanningSessionId] = useState<string | null>(null);
  const [venueSuggestions, setVenueSuggestions] = useState<PlaceSuggestion[] | null>(null);

  const [isStreaming, setIsStreaming] = useState(false);
  const streamAbortRef = useRef<AbortController | null>(null);

  const resumedRef = useRef(false);
  const lastResetKeyRef = useRef(resetKey);

  // Reset session state when resetKey changes (e.g. user logs out and back in)
  useEffect(() => {
    if (lastResetKeyRef.current !== resetKey) {
      lastResetKeyRef.current = resetKey;
      resumedRef.current = false;
      setMessages([]);
      setSuggestedAction(null);
      setPlanningTurn(null);
      setMaxPlanningTurns(null);
      setEventPlan(null);
      setConfirmError(null);
      setExternalResults(null);
      setPlanningSessionId(null);
      setVenueSuggestions(null);
    }
  }, [resetKey]);

  useEffect(() => {
    if (resumedRef.current) return;
    resumedRef.current = true;
    let cancelled = false;

    void (async () => {
      try {
        const session = await resumeSessionApi();
        if (cancelled || !session || !session.messages?.length) return;

        setMessages(session.messages);
        setPlanningSessionId(session.planningSessionId ?? null);
        setPlanningTurn(session.planningTurn ?? null);
        setMaxPlanningTurns(session.maxPlanningTurns ?? null);
        setSuggestedAction(session.suggestedAction ?? null);

        if (session.suggestedAction?.type === 'create_event') {
          setEventPlan(session.suggestedAction.payload as EventPlanPayload);
        }
      } catch {
        // Non-fatal — just start fresh
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function sendMessage(
    content: string,
    contextHint?: string,
    userLocation?: { lat: number; lng: number } | null,
  ): Promise<void> {
    if (!content.trim() || isLoading) return;

    const userMessage: ArkChatMessage = {
      id: generateId(),
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const userTimezone = resolveTimezone();
      const response = await sendArkMessage({ message: content, contextHint, userLocation, userTimezone });

      const arkMessage: ArkChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: response.reply,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, arkMessage]);
      setSuggestedAction(response.suggestedAction ?? null);
      setPlanningTurn(response.planningTurn ?? null);
      setMaxPlanningTurns(response.maxPlanningTurns ?? null);
      setPlanningSessionId(response.planningSessionId ?? null);

      if (response.externalResults) {
        setExternalResults(response.externalResults);
      }

      if (response.venueSuggestions) {
        setVenueSuggestions(response.venueSuggestions);
      }

      if (response.suggestedAction?.type === 'create_event') {
        const p = response.suggestedAction.payload as EventPlanPayload;
        setEventPlan((prev) => ({
          activityType:   p.activityType   ?? prev?.activityType   ?? null,
          locationName:   p.locationName   ?? prev?.locationName   ?? null,
          groupSize:      p.groupSize      ?? prev?.groupSize      ?? null,
          timePreference: p.timePreference ?? prev?.timePreference ?? null,
          eventTitle:     p.eventTitle     ?? prev?.eventTitle     ?? null,
          timeStartISO:   p.timeStartISO   ?? prev?.timeStartISO   ?? null,
          atmosphere:     p.atmosphere     ?? prev?.atmosphere     ?? null,
          externalVenue:  p.externalVenue  ?? prev?.externalVenue  ?? null,
          agePrefMin:         p.agePrefMin         ?? prev?.agePrefMin         ?? null,
          agePrefMax:         p.agePrefMax         ?? prev?.agePrefMax         ?? null,
          agePrefDealbreaker: p.agePrefDealbreaker ?? prev?.agePrefDealbreaker ?? null,
          placeId:            p.placeId            ?? prev?.placeId            ?? null,
          placeAddress:       p.placeAddress       ?? prev?.placeAddress       ?? null,
          placeCategory:      p.placeCategory      ?? prev?.placeCategory      ?? null,
          skillPreferences:   p.skillPreferences   ?? prev?.skillPreferences   ?? null,
          matchIntent:        p.matchIntent        ?? prev?.matchIntent        ?? null,
          format:             p.format             ?? prev?.format             ?? 'in_person',
          zoomUrl:            p.zoomUrl            ?? prev?.zoomUrl            ?? null,
        }));
        setConfirmError(null);
      }
    } catch {
      setError('Ark is unavailable right now. Try again in a moment.');
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Streams the Ark response token-by-token via SSE. Falls back to
   * non-streaming sendMessage on error.
   */
  const sendMessageStreaming = useCallback(async (
    content: string,
    contextHint?: string,
    userLocation?: { lat: number; lng: number } | null,
  ): Promise<void> => {
    if (!content.trim() || isLoading || isStreaming) return;

    const userMessage: ArkChatMessage = {
      id: generateId(),
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };

    // Create a placeholder assistant message that we'll accumulate into
    const assistantId = generateId();
    const assistantMessage: ArkChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    // Defer setIsStreaming(true) until the assistant message is ready to
    // reveal — see the length-scaled dwell at the `done` event below. While
    // we're buffering chunks privately, isLoading=true + isStreaming=false
    // keeps the typing-dots indicator visible (UI gates on that pair).
    setIsLoading(true);
    setError(null);
    const sendStartedAt = Date.now();

    const abortController = new AbortController();
    streamAbortRef.current = abortController;

    try {
      const apiBase = getApiBase();
      const storage = getStorage();
      const token = await resolveStorageValue(storage.getItem('ark_token'));

      const resp = await fetch(`${apiBase}/cleo/chat-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: content, contextHint, userLocation, userTimezone: resolveTimezone() }),
        signal: abortController.signal,
      });

      if (!resp.ok) {
        throw new Error(`Stream request failed: ${resp.status}`);
      }

      const reader = resp.body?.getReader();
      if (!reader) throw new Error('No readable stream');

      const decoder = new TextDecoder();
      let buffer = '';
      let fullReply = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        // Keep the last (potentially incomplete) line in the buffer
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const data = JSON.parse(jsonStr);

            if (data.error) {
              throw new Error(data.error);
            }

            if (data.chunk) {
              // Buffer privately. We deliberately don't surface the partial
              // content to messages while the dots are still showing —
              // the dots-into-full-bubble swap is a single transition,
              // length-scaled to feel composed (see `done` branch).
              fullReply += data.chunk;
            }

            if (data.done) {
              // Final event — process metadata
              const meta = data as ArkChatResponse & { fullReply: string };

              // Use the server's parsed reply (which may have appended notes) if available
              const finalReply = meta.fullReply || fullReply;

              // Length-scaled "thinking" dwell so the dots feel intentional
              // rather than a flash. One-line replies (~100 chars) wait
              // ~350-450ms; a paragraph (~400 chars) waits ~1.2s; capped at
              // 1.5s so people aren't kept waiting on long responses. The
              // network round-trip already counts toward dwell — we only
              // sleep the remainder, so very-fast LLM responses feel
              // composed and slow ones feel snappy on the swap.
              const targetDwellMs = Math.max(350, Math.min(1500, finalReply.length * 4));
              const elapsedMs = Date.now() - sendStartedAt;
              const remainingMs = Math.max(0, targetDwellMs - elapsedMs);
              if (remainingMs > 0) {
                await new Promise<void>((resolve) => setTimeout(resolve, remainingMs));
              }

              // Reveal in one swap. setIsStreaming(true) flips the dots off;
              // the bubble's content arrives on the same tick.
              setIsStreaming(true);
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId ? { ...m, content: finalReply } : m,
                ),
              );

              setSuggestedAction(meta.suggestedAction ?? null);
              setPlanningTurn(meta.planningTurn ?? null);
              setMaxPlanningTurns(meta.maxPlanningTurns ?? null);
              setPlanningSessionId(meta.planningSessionId ?? null);

              if (meta.externalResults) {
                setExternalResults(meta.externalResults);
              }
              if (meta.venueSuggestions) {
                setVenueSuggestions(meta.venueSuggestions);
              }

              if (meta.suggestedAction?.type === 'create_event') {
                const p = meta.suggestedAction.payload as EventPlanPayload;
                setEventPlan((prev) => ({
                  activityType:   p.activityType   ?? prev?.activityType   ?? null,
                  locationName:   p.locationName   ?? prev?.locationName   ?? null,
                  groupSize:      p.groupSize      ?? prev?.groupSize      ?? null,
                  timePreference: p.timePreference ?? prev?.timePreference ?? null,
                  eventTitle:     p.eventTitle     ?? prev?.eventTitle     ?? null,
                  timeStartISO:   p.timeStartISO   ?? prev?.timeStartISO   ?? null,
                  atmosphere:     p.atmosphere     ?? prev?.atmosphere     ?? null,
                  externalVenue:  p.externalVenue  ?? prev?.externalVenue  ?? null,
                  agePrefMin:         p.agePrefMin         ?? prev?.agePrefMin         ?? null,
                  agePrefMax:         p.agePrefMax         ?? prev?.agePrefMax         ?? null,
                  agePrefDealbreaker: p.agePrefDealbreaker ?? prev?.agePrefDealbreaker ?? null,
                  placeId:            p.placeId            ?? prev?.placeId            ?? null,
                  placeAddress:       p.placeAddress       ?? prev?.placeAddress       ?? null,
                  placeCategory:      p.placeCategory      ?? prev?.placeCategory      ?? null,
                  skillPreferences:   p.skillPreferences   ?? prev?.skillPreferences   ?? null,
                  matchIntent:        p.matchIntent        ?? prev?.matchIntent        ?? null,
                  format:             p.format             ?? prev?.format             ?? 'in_person',
                  zoomUrl:            p.zoomUrl            ?? prev?.zoomUrl            ?? null,
                }));
                setConfirmError(null);
              }
            }
          } catch (parseErr) {
            // If it's a re-thrown error from data.error, propagate it
            if (parseErr instanceof Error && parseErr.message !== 'Unexpected end of JSON input') {
              throw parseErr;
            }
            // Otherwise skip malformed SSE lines
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return; // User cancelled

      console.error('[useCleo] Streaming failed, falling back:', err);
      // Remove the placeholder assistant message
      setMessages((prev) => prev.filter((m) => m.id !== assistantId));
      setIsStreaming(false);
      setIsLoading(false);
      // Fall back to non-streaming
      await sendMessage(content, contextHint, userLocation);
      return;
    } finally {
      setIsStreaming(false);
      setIsLoading(false);
      streamAbortRef.current = null;
    }
  }, [isLoading, isStreaming]); // eslint-disable-line react-hooks/exhaustive-deps

  function updateEventPlan(updates: Partial<EventPlanPayload>): void {
    setEventPlan((prev) => prev ? { ...prev, ...updates } : null);
  }

  async function selectExternal(item: YelpVenue | TicketmasterEvent): Promise<void> {
    if (planningSessionId) {
      try {
        await selectExternalVenue(planningSessionId, item as unknown as Record<string, unknown>);
      } catch {
        // Non-fatal
      }
    }
    setEventPlan((prev) => prev ? {
      ...prev,
      locationName: 'name' in item ? item.name : prev.locationName,
      externalVenue: item,
    } : null);
    setExternalResults(null);
  }

  async function skipExternal(): Promise<void> {
    if (planningSessionId) {
      try {
        await skipExternalSearch(planningSessionId);
      } catch {
        // Non-fatal
      }
    }
    setExternalResults(null);
  }

  async function selectVenue(suggestion: PlaceSuggestion): Promise<void> {
    if (planningSessionId) {
      try {
        await selectVenueFromSuggestions(planningSessionId, suggestion.placeId);
      } catch {
        // Non-fatal
      }
    }
    setEventPlan((prev) => {
      if (!prev) return null;
      // Update title to reflect the selected venue if the old location was in the title
      let updatedTitle = prev.eventTitle;
      if (updatedTitle && prev.locationName) {
        updatedTitle = updatedTitle.replace(prev.locationName, suggestion.name);
      }
      return {
        ...prev,
        locationName: suggestion.name,
        eventTitle: updatedTitle,
        placeId: suggestion.placeId,
        placeAddress: suggestion.address ?? null,
        placeCategory: suggestion.category ?? null,
      };
    });
    setVenueSuggestions(null);
    // Trigger a chat turn so handleArkChat sees placeId is set, completes the
    // session, and returns the event plan card
    await sendMessage(`I'd like to go to ${suggestion.name}.`);
  }

  async function skipVenueSuggestions(): Promise<void> {
    setVenueSuggestions(null);
    // Clear locationName from session so resolution doesn't re-trigger with the same name
    if (planningSessionId) {
      try {
        await rejectVenuesApi(planningSessionId);
      } catch { /* non-fatal */ }
    }
    // Tell Ark the user rejected all suggestions so it asks for a different location
    await sendMessage('None of these venues are what I\'m looking for. Can you help me find something else?');
  }

  async function confirmPlan(userLocation?: { lat: number; lng: number } | null): Promise<EventPromptWithCandidates | null> {
    if (!eventPlan) return null;
    if (isConfirming) return null; // Prevent double-clicks
    setIsConfirming(true);
    setConfirmError(null);

    try {
      const timeStart = eventPlan.timeStartISO ?? '';
      if (!timeStart) {
        setConfirmError('Please select a specific date and time before confirming.');
        setIsConfirming(false);
        return null;
      }

      const activityType = (
        VALID_ACTIVITY_TYPES.includes(eventPlan.activityType ?? '')
          ? eventPlan.activityType
          : 'other'
      ) as ActivityType;

      const result = await createEventPrompt({
        activityType,
        activityLabel: eventPlan.eventTitle ?? undefined,
        atmosphere: (eventPlan.atmosphere as import('../types/user.types').AtmosphereType) ?? undefined,
        timeStart,
        locationName: eventPlan.locationName ?? undefined,
        location: userLocation ?? undefined,
        preferredGroupSize: eventPlan.groupSize ?? 4,
        minGroupSize: eventPlan.groupSize ?? 4,
        ...(eventPlan.externalVenue ? {
          externalData: eventPlan.externalVenue as unknown as Record<string, unknown>,
        } : {}),
        ...(eventPlan.placeId ? { placeId: eventPlan.placeId } : {}),
        agePrefMin: eventPlan.agePrefMin ?? undefined,
        agePrefMax: eventPlan.agePrefMax,
        agePrefDealbreaker: eventPlan.agePrefDealbreaker ?? undefined,
        ...(eventPlan.skillPreferences ? { skillPreferences: eventPlan.skillPreferences } : {}),
        // Cleo intent first-class — strings narrowed to the union types at
        // payload-receive (the matching service tolerates unknown values by
        // ignoring them, so a string mismatch can't break a valid plan).
        ...(eventPlan.matchIntent
          ? { matchIntent: eventPlan.matchIntent as import('../types/event_prompt.types').MatchIntent }
          : {}),
        // Phase B4 — pass through the room format. Default 'in_person' is
        // also enforced server-side; passing explicitly avoids surprises.
        format: eventPlan.format ?? 'in_person',
        // Phase C2 — Zoom URL when format='remote'. Server narrows to NULL
        // when format='in_person' regardless, but we send it for fidelity.
        ...(eventPlan.zoomUrl ? { zoomUrl: eventPlan.zoomUrl } : {}),
      });

      // Capture the session ID before we clear local state — the cleanup
      // call below still needs it.
      const sessionToComplete = planningSessionId;

      // Wipe the entire local conversation surface the moment the request
      // is dispatched. The user has handed the plan off; navigating back to
      // /cleo should land on a fresh slate, not the prior conversation
      // (with its plan card still rendered). `clearChat` resets messages,
      // suggestedAction, eventPlan, planningSessionId, externalResults,
      // venueSuggestions, and both error states in one go.
      clearChat();

      // Backend cleanup. Two prongs so the resume endpoint can't resurrect
      // this session on the next mount:
      //   1. If we have a session id, call complete with the prompt id —
      //      sets source_event_prompt_id so resume short-circuits to null.
      //   2. As a backstop (and the only option when no local session id is
      //      tracked, e.g. demo flow that never registered one), abandon
      //      every still-active/completed session for this user. Both calls
      //      are non-fatal — failure must not block the navigation.
      if (sessionToComplete) {
        try { await completeSessionApi(sessionToComplete, result.id); } catch { /* non-fatal */ }
      } else {
        try { await abandonActiveSessionApi(); } catch { /* non-fatal */ }
      }

      return result;
    } catch (err) {
      setConfirmError(err instanceof Error ? err.message : 'Failed to create event. Try again.');
      return null;
    } finally {
      setIsConfirming(false);
    }
  }

  function clearChat(): void {
    setMessages([]);
    setError(null);
    setSuggestedAction(null);
    setPlanningTurn(null);
    setMaxPlanningTurns(null);
    setEventPlan(null);
    setConfirmError(null);
    setExternalResults(null);
    setPlanningSessionId(null);
    setVenueSuggestions(null);
  }

  async function discardPlan(): Promise<void> {
    // Abandon the backend session so nothing persists
    if (planningSessionId) {
      try {
        await abandonSessionApi(planningSessionId);
      } catch {
        // Fallback: try to abandon whatever active session exists
        try { await abandonActiveSessionApi(); } catch { /* non-fatal */ }
      }
    } else {
      // No local session ID — still try to clear any active backend session
      try { await abandonActiveSessionApi(); } catch { /* non-fatal */ }
    }
    clearChat();
  }

  return {
    messages,
    sendMessage,
    sendMessageStreaming,
    isLoading,
    isStreaming,
    error,
    clearChat,
    discardPlan,
    suggestedAction,
    planningTurn,
    maxPlanningTurns,
    eventPlan,
    updateEventPlan,
    confirmPlan,
    isConfirming,
    confirmError,
    externalResults,
    selectExternal,
    skipExternal,
    venueSuggestions,
    selectVenue,
    skipVenueSuggestions,
  };
}
